import { CellWithEllipsis } from '@components/DataTable/CellWithEllipsis';
import { CurrencyCell } from '@components/DataTable/CurrencyCell';
import { RutCell } from '@components/DataTable/RutCell';
import { DateDisplay } from '@components/DateDisplay';
import { ActionCell } from '@components/Documents/ActionCell';
import { SiiStatusContainer } from '@components/Documents/SiiStatusContainer';
import { getDocumentBalance } from '@domain/documents/getDocumentBalance';
import { type DocumentState } from '@store/skeletor/documents/states/getDocumentStates/types';
import { type Document } from '@store/skeletor/documents/types';
import { type ColumnDef } from '@tanstack/react-table';
import { relativeNowDate } from '@utils/date/relativeNowDate';

const columns: (
  options: DocumentState[],
) => ColumnDef<Document>[] = options => [
  {
    id: 'clientName',
    accessorKey: 'clientName',
    enableSorting: false,
    header: 'Cliente',
    cell: CellWithEllipsis,
  },
  {
    id: 'clientRut',
    accessorKey: 'clientRut',
    cell: RutCell,
    header: 'Rut cliente',
    size: 130,
  },
  {
    id: 'operationNumber',
    accessorKey: 'operationNumber',
    header: 'Nr operación',
  },
  {
    id: 'documentNumber',
    accessorKey: 'documentNumber',
    header: 'Nr dcto.',
  },
  {
    id: 'documentType',
    accessorKey: 'documentType',
    enableSorting: false,
    header: 'Tipo dcto.',
    cell: CellWithEllipsis,
  },
  {
    id: 'documentState',
    accessorKey: 'documentState',
    enableSorting: false,
    header: 'Estado',
    cell: ({ row }) => {
      const { documentState } = row.original;
      const stateDescription = options.find(
        option => option.stateValue === documentState,
      );

      return <p className="capitalize">{stateDescription?.stateDescription}</p>;
    },
  },
  {
    id: 'siiStatus',
    accessorKey: 'siiStatus',
    header: 'Estado SII',
    cell: ({ getValue }) => {
      return <SiiStatusContainer siiStatusCode={getValue() as string} />;
    },
  },
  {
    id: 'debtorName',
    accessorKey: 'debtorName',
    enableSorting: false,
    header: 'Deudor',
    cell: CellWithEllipsis,
  },
  {
    id: 'debtorRut',
    accessorKey: 'debtorRut',
    cell: RutCell,
    header: 'Rut deudor',
  },
  {
    id: 'expired',
    accessorKey: 'expired',
    enableSorting: false,
    header: 'Vencido',
    cell: ({ row }) => {
      return row.original.dueDate !== null
        ? relativeNowDate(new Date(row.original.dueDate))
        : '';
    },
  },
  {
    id: 'dueDate',
    accessorKey: 'dueDate',
    header: 'Fecha vencimiento',
    cell: ({ row }) => {
      return <DateDisplay date={row.original.dueDate} format="short" />;
    },
  },
  {
    id: 'documentAmount',
    accessorKey: 'documentAmount',
    header: 'Valor dcto.',
    cell: CurrencyCell,
  },
  {
    id: 'documentAdvance',
    accessorKey: 'documentAdvance',
    header: 'Valor adeudado',
    cell: CurrencyCell,
  },
  {
    id: 'balance',
    header: 'Saldo',
    enableSorting: false,
    accessorFn: getDocumentBalance,
    cell: CurrencyCell,
  },
  {
    id: 'payment',
    accessorKey: 'documentPayment',
    header: 'Pagado',
    cell: CurrencyCell,
  },
  {
    id: 'surplus',
    accessorKey: 'documentSurplus',
    header: 'Excedente',
    cell: CurrencyCell,
  },
  {
    id: 'arrearsInterest',
    accessorKey: 'arrearsInterest',
    header: 'Int. mora',
    cell: CurrencyCell,
  },
  {
    id: 'dueDateOrigin',
    accessorKey: 'originDueDate',
    header: 'Fecha vcto. original',
    cell: ({ row }) => {
      return <DateDisplay date={row.original.originDueDate} format="short" />;
    },
  },
  {
    id: 'paymentDate',
    accessorKey: 'paymentDate',
    header: 'Fecha pago',
    cell: ({ row }) => {
      return <DateDisplay date={row.original.paymentDate} format="short" />;
    },
  },
  {
    id: 'actions',
    header: 'Cobranza',
    enableSorting: false,
    cell: ({ row }) => {
      const { clientRut, documentNumber, documentType, documentState } =
        row.original;
      const stateDescription = options.find(
        option => option.stateValue === documentState,
      );

      return (
        <ActionCell
          documentNumber={documentNumber}
          clientRut={clientRut}
          documentType={documentType}
          documentState={stateDescription?.stateDescription ?? ''}
        />
      );
    },
  },
];

export default columns;
